import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as homeworksService from "../services/homeworks";
import * as programTestsService from "../services/programTests";
import "./ProgramTestSubmissions.css";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateTime(value, locale) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}

// Scores like 0.6666… shown as 0.67 (whole numbers stay whole).
function formatPoints(value) {
  const n = Number(value) || 0;
  return String(Math.round(n * 100) / 100);
}

// True when the student left the question blank.
function isEmptyAnswer(value) {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value)) return value.every(isEmptyAnswer);
  if (typeof value === "object") return Object.values(value).every(isEmptyAnswer);
  return false;
}

// Questions the teacher grades by hand (no automatic answer key).
const MANUAL_TYPES = new Set(["long_text", "essay", "task"]);

function normalizeItems(raw, { getId, getText }) {
  const items = Array.isArray(raw) ? raw : [];
  return items
    .map((it, idx) => {
      const id = String(getId(it, idx) ?? "").trim();
      if (!id) return null;
      return { id, text: String(getText(it, idx) ?? "").trim() };
    })
    .filter(Boolean);
}

function parseTemplate(template) {
  const raw = String(template || "");
  const parts = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let lastIndex = 0;
  let match;
  while ((match = re.exec(raw))) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", value: raw.slice(lastIndex, match.index) });
    }
    parts.push({ type: "blank", id: String(match[1]).trim() });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < raw.length) {
    parts.push({ type: "text", value: raw.slice(lastIndex) });
  }
  return parts;
}

function renderKeyValueRows(rows) {
  const safe = asArray(rows)
    .map((r) => (r ? { k: String(r.k || "").trim(), v: r.v } : null))
    .filter((r) => r && r.k);
  if (!safe.length) return null;
  return (
    <dl className="test-submissions__kv">
      {safe.map((r) => (
        <div className="test-submissions__kvRow" key={r.k}>
          <dt className="test-submissions__kvKey">{r.k}</dt>
          <dd className="test-submissions__kvVal">{r.v}</dd>
        </div>
      ))}
    </dl>
  );
}

function AnswerBody({ exercise, answer, tFn }) {
  const type = String(exercise?.exercise_type || "");
  const config = exercise?.config && typeof exercise.config === "object" ? exercise.config : {};

  if (!type) {
    return (
      <div className="test-submissions__answerBody">
        {tFn("testsModule.submissions.unknownExercise", {
          defaultValue: "Unknown exercise",
        })}
      </div>
    );
  }

  if (type === "stimulus") {
    return (
      <div className="test-submissions__answerBody">
        {tFn("testsModule.submissions.noAnswerExpected", {
          defaultValue: "No answer expected.",
        })}
      </div>
    );
  }

  if (type === "multiple_choice") {
    const options = normalizeItems(config?.options, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.text ?? "",
    });
    const selected = asArray(answer?.selected_option_ids).map(String);
    const selectedText = selected
      .map((id) => options.find((o) => o.id === id))
      .filter(Boolean)
      .map((o) => o.text || o.id);

    return (
      <div className="test-submissions__answerBody">
        {selectedText.length ? (
          <ul className="test-submissions__listPlain">
            {selectedText.map((txt, idx) => (
              <li key={`${idx}-${txt}`}>{txt}</li>
            ))}
          </ul>
        ) : (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noSelection", {
              defaultValue: "No selection",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "true_false") {
    const v = answer?.value;
    return (
      <div className="test-submissions__answerBody">
        {typeof v === "boolean" ? (
          <strong>
            {v
              ? tFn("testsModule.runner.true", { defaultValue: "True" })
              : tFn("testsModule.runner.false", { defaultValue: "False" })}
          </strong>
        ) : (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "short_text" || type === "long_text" || type === "task" || type === "essay") {
    const text = String(answer?.text ?? "");
    return (
      <div className="test-submissions__answerBody">
        {text.trim() ? (
          <div className="test-submissions__textAnswer">{text}</div>
        ) : (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "fill_blanks") {
    const template = String(config?.template || "");
    const parts = parseTemplate(template);
    const values =
      answer?.values && typeof answer.values === "object" ? answer.values : {};

    const seen = new Set();
    const orderedBlankIds = parts
      .filter((p) => p.type === "blank")
      .map((p) => String(p.id || "").trim())
      .filter((id) => {
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });

    const ids = orderedBlankIds.length
      ? orderedBlankIds
      : Object.keys(values || {}).map(String).sort();

    const rows = ids
      .map((id) => ({
        k: tFn("testsModule.submissions.blank", {
          defaultValue: "Blank {{id}}",
          id,
        }),
        v: String(values?.[id] ?? ""),
      }))
      .filter((r) => String(r.v || "").trim().length > 0);

    return (
      <div className="test-submissions__answerBody">
        {renderKeyValueRows(rows) || (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "match_pairs" || type === "connect_arrow") {
    const left = normalizeItems(config?.left, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.text ?? "",
    });
    const right = normalizeItems(config?.right, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.text ?? "",
    });

    const pairs = answer?.pairs && typeof answer.pairs === "object" ? answer.pairs : {};
    const rows = left
      .map((l) => {
        const rightId = String(pairs?.[l.id] ?? "").trim();
        const r = right.find((x) => x.id === rightId);
        const leftLabel = l.text || l.id;
        const rightLabel = r ? r.text || r.id : rightId;
        return rightId
          ? { k: leftLabel, v: rightLabel || rightId }
          : null;
      })
      .filter(Boolean);

    return (
      <div className="test-submissions__answerBody">
        {renderKeyValueRows(rows) || (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "select_words") {
    const items = normalizeItems(config?.items, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.text ?? "",
    });
    const selected = asArray(answer?.selected_item_ids).map(String);
    const selectedText = selected
      .map((id) => items.find((x) => x.id === id))
      .filter(Boolean)
      .map((x) => x.text || x.id);

    return (
      <div className="test-submissions__answerBody">
        {selectedText.length ? (
          <ul className="test-submissions__listPlain">
            {selectedText.map((txt, idx) => (
              <li key={`${idx}-${txt}`}>{txt}</li>
            ))}
          </ul>
        ) : (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noSelection", {
              defaultValue: "No selection",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "diagram_label") {
    const items = normalizeItems(config?.items, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.label ?? it?.text ?? "",
    });
    const values =
      answer?.values && typeof answer.values === "object" ? answer.values : {};
    const rows = (items.length ? items : Object.keys(values || {}).map((id) => ({ id, text: id })))
      .map((it) => {
        const v = String(values?.[it.id] ?? "");
        return v.trim() ? { k: it.text || it.id, v } : null;
      })
      .filter(Boolean);

    return (
      <div className="test-submissions__answerBody">
        {renderKeyValueRows(rows) || (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "image_label") {
    const hotspots = normalizeItems(config?.hotspots, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.label ?? "",
    });
    const labels = normalizeItems(config?.labels, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.text ?? "",
    });
    const placements =
      answer?.placements && typeof answer.placements === "object" ? answer.placements : {};

    const rows = (hotspots.length ? hotspots : Object.keys(placements || {}).map((id) => ({ id, text: id })))
      .map((hs) => {
        const labelId = String(placements?.[hs.id] ?? "").trim();
        if (!labelId) return null;
        const label = labels.find((l) => l.id === labelId);
        const key = hs.text ? `${hs.text} (${hs.id})` : `Hotspot ${hs.id}`;
        const val = label ? label.text || label.id : labelId;
        return { k: key, v: val };
      })
      .filter(Boolean);

    return (
      <div className="test-submissions__answerBody">
        {renderKeyValueRows(rows) || (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "drag_drop") {
    const mode = String(config?.mode || "order");
    const items = normalizeItems(config?.items, {
      getId: (it, idx) => it?.id ?? idx + 1,
      getText: (it) => it?.text ?? "",
    });

    if (mode === "categorize") {
      const categories = normalizeItems(config?.categories, {
        getId: (it, idx) => it?.id ?? idx + 1,
        getText: (it) => it?.label ?? it?.text ?? "",
      });
      const map =
        answer?.category_by_item_id && typeof answer.category_by_item_id === "object"
          ? answer.category_by_item_id
          : {};

      const rows = items
        .map((it) => {
          const categoryId = String(map?.[it.id] ?? "").trim();
          if (!categoryId) return null;
          const c = categories.find((x) => x.id === categoryId);
          return { k: it.text || it.id, v: c ? c.text || c.id : categoryId };
        })
        .filter(Boolean);

      return (
        <div className="test-submissions__answerBody">
          {renderKeyValueRows(rows) || (
            <div className="test-submissions__muted">
              {tFn("testsModule.submissions.noAnswer", {
                defaultValue: "No answer",
              })}
            </div>
          )}
        </div>
      );
    }

    const ordered = asArray(answer?.ordered_item_ids).map(String);
    const defaultOrder = items.map((it) => it.id);
    const actualOrder = ordered.length
      ? ordered.filter((id) => defaultOrder.includes(id))
      : defaultOrder;

    return (
      <div className="test-submissions__answerBody">
        {actualOrder.length ? (
          <ol className="test-submissions__listOrdered">
            {actualOrder.map((id) => {
              const it = items.find((x) => x.id === id) || { id, text: id };
              return <li key={id}>{it.text || it.id}</li>;
            })}
          </ol>
        ) : (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  if (type === "data_interpretation") {
    const stem = String(config?.stem ?? "").trim();
    const items = Array.isArray(config?.items) ? config.items : [];
    const answersById =
      answer?.answers_by_item_id && typeof answer.answers_by_item_id === "object"
        ? answer.answers_by_item_id
        : {};

    return (
      <div className="test-submissions__answerBody">
        {stem ? <div className="test-submissions__note">{stem}</div> : null}
        {items.length ? (
          <div className="test-submissions__subAnswers">
            {items.map((it, idx) => {
              const itemId = String(it?.id ?? idx + 1);
              const childExercise = {
                id: itemId,
                exercise_type: String(it?.type || it?.exercise_type || "multiple_choice"),
                prompt: String(it?.prompt || ""),
                points: Number(it?.points ?? 1),
                config:
                  typeof it?.config === "object" && it?.config ? it.config : {},
              };
              return (
                <div className="test-submissions__subAnswer" key={itemId}>
                  <div className="test-submissions__subPrompt">
                    <strong>{idx + 1}.</strong> {childExercise.prompt}
                  </div>
                  <AnswerBody
                    exercise={childExercise}
                    answer={answersById?.[itemId]}
                    tFn={tFn}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="test-submissions__muted">
            {tFn("testsModule.submissions.noAnswer", {
              defaultValue: "No answer",
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="test-submissions__answerBody">
      <div className="test-submissions__muted">
        {tFn("testsModule.submissions.unsupportedAnswer", {
          defaultValue: "Answer captured but cannot be displayed for this question type.",
        })}
      </div>
    </div>
  );
}

function clampNumber(value, min, max) {
  const n = Number(value);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n;
}

// Where the copies come from: a test (default) or a homework. Both share the
// same exercise/answer/grading shapes, so the correction screen is the same.
const SOURCES = {
  test: {
    load: ({ accessToken, id }) =>
      Promise.all([
        programTestsService.fetchProgramTest({ accessToken, testId: id }),
        programTestsService.fetchProgramTestSubmissions({ accessToken, testId: id }),
      ]),
    save: (sub, args) =>
      String(sub?.kind || "") === "lead"
        ? programTestsService.updateProgramTestLeadSubmission(args)
        : programTestsService.updateProgramTestSubmission(args),
    listPath: "/tests",
    editPath: (id) => `/tests/${id}/edit`,
  },
  homework: {
    load: ({ accessToken, id }) =>
      Promise.all([
        homeworksService.fetchHomework({
          accessToken,
          homeworkId: id,
          params: { include_exercises: "true" },
        }),
        homeworksService.fetchHomeworkSubmissions({ accessToken, homeworkId: id }),
      ]),
    save: (sub, args) => homeworksService.updateHomeworkSubmission(args),
    listPath: "/homeworks",
    editPath: (id) => `/homeworks/${id}/edit`,
  },
};

export default function ProgramTestSubmissions({ kind = "test" } = {}) {
  const params = useParams();
  const source = SOURCES[kind] || SOURCES.test;
  const isHomework = kind === "homework";
  const testId = isHomework ? params.homeworkId : params.testId;
  const { accessToken, effectiveRole } = useAuth();
  const { language, t } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const tFn = useCallback(
    (key, options = {}) => {
      const defaultValue = options.defaultValue;
      return t(key, { defaultValue, ...options });
    },
    [t],
  );

  const isTeacherOrAdmin =
    effectiveRole === "teacher" ||
    ["admin", "superadmin"].includes(effectiveRole);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [test, setTest] = useState(null);
  const [submissions, setSubmissions] = useState([]);

  // Local correction state: { [submissionId]: { [exerciseId]: awardedPoints } }
  const [awardedPoints, setAwardedPoints] = useState({});
  const [savingSubmissionId, setSavingSubmissionId] = useState(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const [testPayload, submissionsPayload] = await source.load({
          accessToken,
          id: testId,
        });
        if (ignore) return;
        setTest(testPayload);
        setSubmissions(asArray(submissionsPayload?.submissions));
      } catch (e) {
        if (ignore) return;
        setError(
          e?.message ||
            tFn("testsModule.submissions.failedLoad", {
              defaultValue: "Failed to load submissions",
            }),
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    if (accessToken && testId && isTeacherOrAdmin) {
      load();
    } else {
      setLoading(false);
    }

    return () => {
      ignore = true;
    };
  }, [accessToken, isTeacherOrAdmin, source, tFn, testId]);

  const exercisesById = useMemo(() => {
    const map = new Map();
    asArray(test?.exercises).forEach((ex) => {
      if (ex?.id) map.set(ex.id, ex);
    });
    return map;
  }, [test?.exercises]);

  const questionNumberById = useMemo(() => {
    const map = new Map();
    let n = 0;
    [...asArray(test?.exercises)]
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .forEach((ex) => {
        if (String(ex?.exercise_type || "") === "stimulus") return;
        n += 1;
        map.set(ex.id, n);
      });
    return map;
  }, [test?.exercises]);

  const locale = language === "ar" ? "ar-TN" : language || "fr";

  const testMaxScore = useMemo(() => {
    return asArray(test?.exercises).reduce((sum, ex) => {
      const isStimulus = String(ex?.exercise_type || "") === "stimulus";
      const pts = safeNumber(ex?.points, 0);
      return sum + (isStimulus ? 0 : Math.max(0, pts));
    }, 0);
  }, [test?.exercises]);

  const resultsByExerciseIdFor = (sub) => {
    const results = asArray(sub?.grading_summary?.results);
    const map = new Map();
    results.forEach((r) => {
      if (!r) return;
      const id = r.exercise_id;
      if (id == null) return;
      map.set(String(id), r);
    });
    return map;
  };

  const getAwarded = (subOrSubmissionId, exerciseId, fallback) => {
    const isSubmissionObject =
      subOrSubmissionId && typeof subOrSubmissionId === "object";
    const submissionId = isSubmissionObject
      ? subOrSubmissionId.id
      : subOrSubmissionId;

    const s = awardedPoints?.[String(submissionId)];
    if (s && Object.prototype.hasOwnProperty.call(s, String(exerciseId))) {
      return s[String(exerciseId)];
    }

    if (isSubmissionObject) {
      const manual =
        subOrSubmissionId?.grading_summary?.manual_review
          ?.awarded_by_exercise_id;
      if (
        manual &&
        Object.prototype.hasOwnProperty.call(manual, String(exerciseId))
      ) {
        return manual[String(exerciseId)];
      }
    }

    return fallback;
  };

  const setAwarded = (submissionId, exerciseId, value) => {
    setAwardedPoints((prev) => {
      const sid = String(submissionId);
      const eid = String(exerciseId);
      const nextForSub = { ...(prev?.[sid] || {}), [eid]: value };
      return { ...(prev || {}), [sid]: nextForSub };
    });
  };

  const computeTotalsFor = (sub) => {
    const resById = resultsByExerciseIdFor(sub);

    // Corrected before per-question points were kept: show the stored score
    // until the teacher changes something.
    if (
      String(sub?.status || "") === "reviewed" &&
      sub?.score != null &&
      !sub?.grading_summary?.manual_review &&
      !awardedPoints?.[String(sub.id)]
    ) {
      const storedMax = safeNumber(sub?.max_score, testMaxScore) || testMaxScore;
      const storedScore = safeNumber(sub.score, 0);
      return {
        score: storedScore,
        maxScore: storedMax,
        score20: storedMax > 0 ? (storedScore / storedMax) * 20 : 0,
      };
    }

    let score = 0;
    asArray(test?.exercises).forEach((ex) => {
      const exId = ex?.id;
      if (!exId) return;
      const isStimulus = String(ex?.exercise_type || "") === "stimulus";
      if (isStimulus) return;

      const maxPts = Math.max(0, safeNumber(ex?.points, 0));
      const autoScore = safeNumber(resById.get(String(exId))?.score, 0);
      const awarded = safeNumber(
        getAwarded(sub, exId, autoScore),
        autoScore,
      );
      score += clampNumber(awarded, 0, maxPts);
    });

    const max = safeNumber(sub?.max_score, testMaxScore);
    const maxScore = max > 0 ? max : testMaxScore;
    const score20 = maxScore > 0 ? (score / maxScore) * 20 : 0;

    return {
      score,
      maxScore,
      score20,
    };
  };

  const saveReview = async (sub) => {
    if (!accessToken) return;
    setSavingSubmissionId(sub.id);
    setError("");

    const totals = computeTotalsFor(sub);
    const resById = resultsByExerciseIdFor(sub);

    const awardedByExerciseId = {};
    asArray(test?.exercises).forEach((ex) => {
      const exId = ex?.id;
      if (!exId) return;
      const isStimulus = String(ex?.exercise_type || "") === "stimulus";
      if (isStimulus) return;
      const maxPts = Math.max(0, safeNumber(ex?.points, 0));
      const autoScore = safeNumber(resById.get(String(exId))?.score, 0);
      const awarded = safeNumber(
        getAwarded(sub, exId, autoScore),
        autoScore,
      );
      awardedByExerciseId[String(exId)] = clampNumber(awarded, 0, maxPts);
    });

    const nextSummary = {
      ...(sub?.grading_summary && typeof sub.grading_summary === "object"
        ? sub.grading_summary
        : {}),
      manual_review: {
        awarded_by_exercise_id: awardedByExerciseId,
        score: String(totals.score),
        max_score: String(totals.maxScore),
      },
    };

    try {
      const updated = await source.save(sub, {
        accessToken,
        submissionId: sub.id,
        payload: {
          score: String(totals.score.toFixed(2)),
          max_score: String(totals.maxScore.toFixed(2)),
          grading_summary: nextSummary,
          status: "reviewed",
        },
      });

      setSubmissions((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s)),
      );
    } catch (e) {
      setError(
        e?.message ||
          tFn("testsModule.submissions.failedSave", {
            defaultValue: "Failed to save review",
          }),
      );
    } finally {
      setSavingSubmissionId(null);
    }
  };

  if (!isTeacherOrAdmin) {
    return (
      <main className="test-submissions" dir={dir}>
        <div className="container test-submissions__container">
          <div className="test-submissions__error">
            {tFn("testsModule.submissions.notAllowed", {
              defaultValue: "Not allowed",
            })}
          </div>
          <Link className="btn ghost" to={source.listPath}>
            {tFn("testsModule.common.back", { defaultValue: "Back" })}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="test-submissions" dir={dir}>
      <div className="container test-submissions__container">
        <header className="test-submissions__header">
          <div>
            <h1 className="test-submissions__title">
              {tFn("testsModule.submissions.title", {
                defaultValue: "Submissions",
              })}{" "}
              {test?.title ? `— ${test.title}` : ""}
            </h1>
            <p className="test-submissions__subtitle">
              {isHomework
                ? tFn("homeworkUi.submissions.subtitle", {
                    defaultValue: "{{count}} copies received",
                    count: submissions.length,
                  })
                : tFn("testsModule.submissions.testNumber", {
                    defaultValue: "Test #{{id}}",
                    id: testId,
                  })}
            </p>
          </div>
          <div className="test-submissions__headerActions">
            <Link className="btn ghost" to={source.listPath}>
              {tFn("testsModule.common.back", { defaultValue: "Back" })}
            </Link>
            <Link className="btn ghost" to={source.editPath(testId)}>
              {tFn("testsModule.common.edit", { defaultValue: "Edit" })}
            </Link>
          </div>
        </header>

        {error ? <div className="test-submissions__error">{error}</div> : null}
        {loading ? (
          <div className="test-submissions__loading">
            {tFn("testsModule.common.loading", { defaultValue: "Loading..." })}
          </div>
        ) : null}

        {!loading && !submissions.length ? (
          <div className="test-submissions__empty">
            {tFn("testsModule.submissions.empty", {
              defaultValue: "No submissions yet.",
            })}
          </div>
        ) : null}

        <div className="test-submissions__list">
          {submissions.map((sub) => (
            <article className="test-submissions__item" key={sub.id}>
              <header className="test-submissions__itemHead">
                <div>
                  <strong>
                    {isHomework
                      ? sub?.student?.full_name || `#${sub.student_id}`
                      : tFn("testsModule.submissions.submissionNumber", {
                          defaultValue: "Submission #{{id}}",
                          id: sub.id,
                        })}
                  </strong>
                  <div className="test-submissions__meta">
                    {String(sub?.kind || "") === "lead" ? (
                      <span className="test-submissions__who">
                        {tFn("testsModule.submissions.leadLabel", {
                          defaultValue: "Lead: {{child}} ({{parent}})",
                          child:
                            sub?.lead?.child_full_name ||
                            sub?.lead?.child ||
                            "",
                          parent:
                            sub?.lead?.parent_full_name ||
                            sub?.lead?.parent ||
                            "",
                        })}
                      </span>
                    ) : isHomework ? null : (
                      <span className="test-submissions__who">
                        {tFn("testsModule.submissions.studentLabel", {
                          defaultValue: "Student: {{name}}",
                          name:
                            sub?.student?.full_name ||
                            String(sub?.student_id ?? ""),
                        })}
                      </span>
                    )}
                    {sub?.parent?.email || sub?.parent?.phone_number ? (
                      <span className="test-submissions__whoDetails">
                        {sub?.parent?.email ? sub.parent.email : null}
                        {sub?.parent?.email && sub?.parent?.phone_number
                          ? " • "
                          : null}
                        {sub?.parent?.phone_number
                          ? sub.parent.phone_number
                          : null}
                      </span>
                    ) : null}
                    {String(sub?.kind || "") === "lead" ? (
                      (() => {
                        const details = [
                          sub?.lead?.parent_email || "",
                          sub?.lead?.parent_phone || "",
                          sub?.lead?.city || "",
                        ]
                          .map((x) => String(x || "").trim())
                          .filter(Boolean)
                          .join(" • ");
                        return details ? (
                          <span className="test-submissions__whoDetails">
                            {details}
                          </span>
                        ) : null;
                      })()
                    ) : null}
                    {sub.submitted_at ? (
                      <span>
                        {tFn("testsModule.submissions.submittedAt", {
                          defaultValue: "Submitted: {{when}}",
                          when: formatDateTime(sub.submitted_at, locale),
                        })}
                      </span>
                    ) : null}
                    {sub?.status ? (
                      <span
                        className={`test-submissions__pill test-submissions__pill--${String(sub.status)}`}
                      >
                        {tFn(`testsModule.submissions.status.${String(sub.status)}`, {
                          defaultValue: String(sub.status),
                        })}
                      </span>
                    ) : null}
                    {sub?.is_late ? (
                      <span className="test-submissions__pill test-submissions__pill--late">
                        {tFn("homeworkUi.late", { defaultValue: "Late" })}
                      </span>
                    ) : null}
                    {sub?.submitted_by?.full_name ? (
                      <span>
                        {tFn("testsModule.submissions.submittedBy", {
                          defaultValue: "By: {{name}}",
                          name: sub.submitted_by.full_name,
                        })}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="test-submissions__score">
                  {(() => {
                    const totals = computeTotalsFor(sub);
                    return (
                      <>
                        <div className="test-submissions__scoreSummary">
                          <strong className="test-submissions__scoreValue">
                            {formatPoints(totals.score)} / {formatPoints(totals.maxScore)}
                          </strong>
                          <span className="test-submissions__scoreOutOf">
                            {tFn("testsModule.submissions.outOf20", {
                              defaultValue: "{{n}} / 20",
                              n: formatPoints(totals.score20),
                            })}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn primary"
                          onClick={() => saveReview(sub)}
                          disabled={savingSubmissionId === sub.id}
                        >
                          {savingSubmissionId === sub.id
                            ? tFn("testsModule.common.saving", {
                                defaultValue: "Saving...",
                              })
                            : tFn("testsModule.submissions.saveReview", {
                                defaultValue: "Save review",
                              })}
                        </button>
                      </>
                    );
                  })()}
                </div>
              </header>

              <details
                className="test-submissions__details"
                open={String(sub?.status || "") !== "reviewed"}
              >
                <summary>
                  {tFn("testsModule.submissions.showAnswers", {
                    defaultValue: "Answers",
                  })}
                </summary>
              <div className="test-submissions__answers">
                {asArray(sub.answers).map((ans) => {
                  const exercise = exercisesById.get(ans.exercise_id);
                  const maxPts = exercise
                    ? Math.max(
                        0,
                        safeNumber(
                          String(exercise.exercise_type || "") === "stimulus"
                            ? 0
                            : exercise.points,
                          0,
                        ),
                      )
                    : 0;
                  const resById = resultsByExerciseIdFor(sub);
                  const autoScore = safeNumber(
                    resById.get(String(ans.exercise_id))?.score,
                    0,
                  );
                  const awarded = getAwarded(
                    sub,
                    ans.exercise_id,
                    autoScore,
                  );

                  const type = String(exercise?.exercise_type || "");
                  const number = questionNumberById.get(ans.exercise_id);
                  let verdict = null;
                  if (exercise && type !== "stimulus") {
                    if (isEmptyAnswer(ans.answer)) verdict = "noAnswer";
                    else if (MANUAL_TYPES.has(type)) verdict = "toGrade";
                    else if (maxPts > 0 && autoScore >= maxPts) verdict = "correct";
                    else if (autoScore > 0) verdict = "partial";
                    else verdict = "wrong";
                  }

                  return (
                    <div
                      className={`test-submissions__answer${
                        verdict ? ` test-submissions__answer--${verdict}` : ""
                      }`}
                      key={ans.id}
                    >
                      <div className="test-submissions__answerPrompt">
                        <strong>
                          {number
                            ? tFn("testsModule.editor.questionN", {
                                defaultValue: "Question {{n}}",
                                n: number,
                              })
                            : tFn("testsModule.editor.typeNames.stimulus", {
                                defaultValue: "Text to read",
                              })}
                        </strong>
                        {type ? (
                          <span className="test-submissions__pill test-submissions__pill--type">
                            {tFn(`testsModule.editor.typeNames.${type}`, {
                              defaultValue: type,
                            })}
                          </span>
                        ) : null}
                        {verdict ? (
                          <span
                            className={`test-submissions__verdict test-submissions__verdict--${verdict}`}
                          >
                            <i
                              className={`fa-solid ${
                                {
                                  correct: "fa-check",
                                  partial: "fa-circle-half-stroke",
                                  wrong: "fa-xmark",
                                  toGrade: "fa-pen",
                                  noAnswer: "fa-minus",
                                }[verdict]
                              }`}
                              aria-hidden="true"
                            />
                            {tFn(`testsModule.submissions.verdict.${verdict}`, {
                              defaultValue: verdict,
                            })}
                          </span>
                        ) : null}
                        {exercise ? (
                          <span className="test-submissions__pill">
                            {tFn("testsModule.submissions.points", {
                              defaultValue: "Points: {{n}}",
                              n: maxPts,
                            })}
                          </span>
                        ) : null}
                        {exercise?.prompt ? (
                          <p className="test-submissions__prompt">
                            {exercise.prompt}
                          </p>
                        ) : null}
                      </div>

                      {exercise &&
                      String(exercise.exercise_type || "") !== "stimulus" ? (
                        <div className="test-submissions__correction">
                          <label>
                            <span>
                              {tFn("testsModule.submissions.awardedPoints", {
                                defaultValue: "Awarded (0–{{max}})",
                                max: maxPts,
                              })}
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.25"
                              max={String(maxPts)}
                              value={
                                typeof awarded === "number"
                                  ? formatPoints(awarded)
                                  : String(awarded ?? "")
                              }
                              onChange={(e) =>
                                setAwarded(
                                  sub.id,
                                  ans.exercise_id,
                                  e.target.value,
                                )
                              }
                            />
                          </label>

                          <div className="test-submissions__auto">
                            {MANUAL_TYPES.has(type)
                              ? tFn("testsModule.submissions.gradeByHand", {
                                  defaultValue: "To grade by hand",
                                })
                              : tFn("testsModule.submissions.autoScore", {
                                  defaultValue: "Auto: {{n}}",
                                  n: formatPoints(autoScore),
                                })}
                          </div>
                        </div>
                      ) : null}

                      {exercise ? (
                        <AnswerBody exercise={exercise} answer={ans.answer} tFn={tFn} />
                      ) : (
                        <div className="test-submissions__answerBody">
                          <div className="test-submissions__muted">
                            {tFn("testsModule.submissions.missingExercise", {
                              defaultValue:
                                "Exercise definition not found for this answer.",
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {!asArray(sub.answers).length ? (
                  <div className="test-submissions__empty">
                    {tFn("testsModule.submissions.noAnswers", {
                      defaultValue: "No answers payload.",
                    })}
                  </div>
                ) : null}
              </div>
              </details>
            </article>
          ))}
        </div>
      </div>
    </main>
  );
}
